import { useMemo, useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { WaterMesh } from 'three/examples/jsm/objects/WaterMesh.js'
import { useShorelineMask } from './useShorelineMask.js'
import {
  time, uniform, float, vec2, mix, smoothstep, texture, positionWorld,
} from 'three/tsl'

// Module-scope constant so the default prop is a STABLE reference across renders.
const DEFAULT_POSITION = [0, 0.5, 0]

// WebGPU ocean, built on three's own WaterMesh.
//
// WaterMesh is three's TSL/NodeMaterial counterpart to the GLSL Water example — its docs state
// plainly that it "can only be used with WebGPURenderer", while Water is the WebGL one. It keeps
// the real planar reflection via TSL's reflector() node, which manages its own reflection target
// internally rather than us hand-rolling a WebGLRenderTarget (the thing that caused the
// "Destroyed texture -msaa" failure and forced RTT out of SceneManager).
//
// Using the library implementation rather than a hand-written port per the ecosystem-first rule.
export default function OceanWaterTSL({
  // NOTE: a default array literal is a NEW object on every render. Never put `position` itself
  // in a useMemo dependency list — depend on its numeric components instead, or the water is
  // rebuilt every frame (seen as the ocean flickering and the foam resetting).
  position = DEFAULT_POSITION,
  animationSpeed = 0.3,
  waterColor = '#001d28',
  sunColor = '#ffffff',
  distortionScale = 1.7,
  size = 50.0,
  foamAmount = 0.11,
  foamDepth = 1.30,
  foamSharpness = 4.5,
  foamColor = '#ffffff',
}) {
  const meshRef = useRef()

  // Top-down distance field baked from the terrain. null until the bake completes.
  const shoreline = useShorelineMask({ maxDistance: 40 })

  // Hold the loading screen until the ocean is genuinely in the scene, so the water and its
  // foam are never seen popping in after the reveal. See SceneReadySignal in app.jsx.
  //
  // The timeout is a safety valve: the shoreline field is a shipped asset, and if it is missing
  // or fails to load the water never mounts. Without this the loading screen would hang at 90%
  // forever — a missing ocean is bad, a permanently stuck site is worse.
  useEffect(() => {
    const bail = setTimeout(() => { window.__oceanReady = true }, 5000)
    return () => clearTimeout(bail)
  }, [])

  // Fires when the water primitive is attached to the scene graph.
  const markOceanReady = () => { window.__oceanReady = true }

  // Procedural tiling normal map — the GLSL Water example loads waternormals.jpg from
  // threejs.org at runtime, a live third-party request on a public site, and the file is not in
  // public/. Smooth value noise over three octaves, converted to a tangent-space normal by
  // finite differences with wrapped lookups so the tile is seamless.
  const waterNormals = useMemo(() => {
    const res = 512
    const data = new Uint8Array(res * res * 4)

    const hash = (x, y) => {
      const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453
      return n - Math.floor(n)
    }

    const valueNoise = (x, y, period) => {
      const xi = Math.floor(x), yi = Math.floor(y)
      const xf = x - xi, yf = y - yi
      const u = xf * xf * (3 - 2 * xf)
      const v = yf * yf * (3 - 2 * yf)
      const w = (a, b) => ((a % b) + b) % b
      const a = hash(w(xi, period), w(yi, period))
      const b = hash(w(xi + 1, period), w(yi, period))
      const c = hash(w(xi, period), w(yi + 1, period))
      const d = hash(w(xi + 1, period), w(yi + 1, period))
      return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v
    }

    const height = (px, py) => (
      valueNoise(px * 16 / res, py * 16 / res, 16) * 0.5 +
      valueNoise(px * 32 / res, py * 32 / res, 32) * 0.32 +
      valueNoise(px * 64 / res, py * 64 / res, 64) * 0.18
    )

    for (let y = 0; y < res; y++) {
      for (let x = 0; x < res; x++) {
        const hL = height((x - 1 + res) % res, y)
        const hR = height((x + 1) % res, y)
        const hD = height(x, (y - 1 + res) % res)
        const hU = height(x, (y + 1) % res)

        const nx = (hL - hR) * 2
        const ny = (hD - hU) * 2
        const len = Math.hypot(nx, ny, 1)

        const i = (y * res + x) * 4
        data[i]     = ((nx / len) * 0.5 + 0.5) * 255
        data[i + 1] = ((ny / len) * 0.5 + 0.5) * 255
        data[i + 2] = ((1 / len) * 0.5 + 0.5) * 255
        data[i + 3] = 255
      }
    }

    const tex = new THREE.DataTexture(data, res, res, THREE.RGBAFormat)
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.generateMipmaps = true
    tex.needsUpdate = true
    return tex
  }, [])

  const water = useMemo(() => {
    if (!shoreline) return null

    const shorelineMask = shoreline.texture
    const maskWorldSize = shoreline.size
    const maskMaxDistance = shoreline.maxDistance
    const maskCentre = shoreline.centre

    const geometry = new THREE.PlaneGeometry(2000, 2000, 128, 128)

    const mesh = new WaterMesh(geometry, {
      waterNormals,
      sunDirection: new THREE.Vector3(1, 1, 1).normalize(),
      sunColor: new THREE.Color(sunColor),
      waterColor: new THREE.Color(waterColor),
      distortionScale,
      // Half-resolution reflection target — the reflection is heavily distorted by the wave
      // normals, so the cost saving is invisible.
      resolutionScale: 0.5,
      alpha: 1.0,
    })

    mesh.rotation.x = -Math.PI / 2
    mesh.position.set(position[0], position[1], position[2])
    // Draw the water AFTER the terrain (renderOrder 1, not -1).
    //
    // Depth-based foam samples the viewport depth texture to find how far the seabed sits below
    // the surface. At renderOrder -1 the water drew FIRST, so that texture did not yet contain
    // the terrain — the mask read stale depth and produced a hard band that followed the water
    // plane's own 128x128 tessellation (visible as triangular sawtooth notches) rather than the
    // coastline. The terrain must be in the depth buffer before the water samples it.
    mesh.renderOrder = 1

    // --- Shoreline foam -------------------------------------------------------------------
    //
    // Driven by a top-down distance field baked once from the terrain (useShorelineMask), so the
    // band is a real world-space distance from the coast and does not inherit the land's
    // on-screen silhouette. This WRAPS WaterMesh's own colorNode rather than replacing it, so
    // three's water shading, reflections and specular are all preserved.
    const foamColorU = uniform(new THREE.Color(foamColor))
    const foamDepthU = uniform(foamDepth)     // world units from the coast that foam reaches
    const foamAmountU = uniform(foamAmount)   // 0 = off, 1 = full foam colour at the waterline
    const foamSharpU = uniform(foamSharpness) // higher = tighter band hugging the shore

    // Sample the pre-baked shoreline distance field by WORLD XZ.
    //
    // The field is rendered once from directly above (see useShorelineMask): 1 at the coastline,
    // falling to 0 maskMaxDistance world units out to sea. Because it is a world-space lookup it
    // has no dependency on the land's on-screen silhouette — which is what made every
    // screen-space depth approach clip against cliff geometry and read as jagged triangle edges.
    // The bake is fitted to the terrain, so it is not necessarily centred on the world origin —
    // offset by its centre before normalising, or the field lands in the wrong place.
    // The bake camera spans -bakeSize/2 .. +bakeSize/2, so dividing the centred world position
    // by bakeSize maps the full captured area onto 0..1. Scale 1.0 and no extra flip here is the
    // combination confirmed correct against the scene — the required flip lives in the loader
    // (texture.flipY), not in this UV maths.
    const maskUv = positionWorld.xz
      .sub(vec2(maskCentre.x, maskCentre.z))
      .div(maskWorldSize)
      .add(0.5)
    const nearness = texture(shorelineMask, maskUv).r   // 1 at the coast -> 0 offshore

    // Convert to distance from land in world units.
    const distFromLand = float(1).sub(nearness).mul(maskMaxDistance)

    // --- Turbulence -------------------------------------------------------------------------
    //
    // Two scrolling noise layers at different scales and speeds, sampled in WORLD XZ so the
    // pattern is anchored to the sea rather than the screen. Both reuse the procedural water
    // normal map (already tiling and seamless) rather than loading another texture.
    const nA = texture(
      waterNormals,
      positionWorld.xz.mul(0.35).add(vec2(time.mul(0.03), time.mul(0.021))),
    ).r
    const nB = texture(
      waterNormals,
      positionWorld.xz.mul(0.11).sub(vec2(time.mul(0.017), time.mul(0.013))),
    ).g

    // Combined, centred on zero so it pushes the shoreline both in and out.
    const turbulence = nA.mul(0.65).add(nB.mul(0.35)).sub(0.5)

    // Warp the DISTANCE, not the final colour. Displacing the field before banding makes the
    // foam edge itself undulate — surging up the beach in places and receding in others —
    // instead of a clean offset outline with noise painted over it.
    //
    // The displacement is deliberately a SMALL fraction of the band width (0.25), and turbulence
    // is centred on zero, so it perturbs the edge without widening the band overall. Scaling it
    // by the full foamDepth made the foam far heavier than the tuned settings intended.
    const warpedDist = distFromLand.add(turbulence.mul(foamDepthU).mul(0.25))

    const shoreBand = smoothstep(foamDepthU, float(0), warpedDist).pow(foamSharpU)

    // Second, finer erosion to break the outer edge into streaks and flecks, while the
    // waterline itself stays solid.
    const fine = texture(
      waterNormals,
      positionWorld.xz.mul(1.4).add(vec2(time.mul(0.05), time.mul(-0.04))),
    ).b

    // Erode only — multiply the band down by the fine noise so the outer edge breaks into
    // flecks. A smoothstep remap here would REDISTRIBUTE the band and could push values up,
    // adding foam rather than removing it.
    const shoreMask = shoreBand.mul(fine.mul(0.5).add(0.5))

    // TEMPORARY: show the raw mask so we can see WHAT it is selecting before styling it.
    // Red = mask 1 (where foam would go), black = mask 0. If the red lands on the submerged
    // land rather than hugging the waterline, the depth comparison is wrong. If red appears
    // in the right place, the problem is purely how the foam is blended.
    const baseColor = mesh.material.colorNode
    const foamStrength = shoreMask.mul(foamAmountU).clamp(0, 1)

    // mix() towards the foam colour, so the water beneath still shows through at partial
    // strength rather than being replaced by flat paint.
    mesh.material.colorNode = mix(baseColor, foamColorU, foamStrength)

    // Expose the uniforms so the Leva controls below can drive them live.
    mesh.userData.foam = { foamColorU, foamDepthU, foamAmountU, foamSharpU }

    return mesh
    // Built once; the tunables below flow through uniforms so the node graph is not recompiled.
  }, [waterNormals, position[0], position[1], position[2], shoreline])

  useEffect(() => {
    const f = water?.userData?.foam
    if (!f) return
    f.foamAmountU.value = foamAmount
    f.foamDepthU.value = foamDepth
    f.foamSharpU.value = foamSharpness
    f.foamColorU.value.set(foamColor)
  }, [water, foamAmount, foamDepth, foamSharpness, foamColor])

  // WaterMesh exposes its uniforms as UniformNodes — assigning .value updates the live shader.
  useEffect(() => { if (water) water.size.value = size }, [water, size])
  useEffect(() => { if (water) water.distortionScale.value = distortionScale }, [water, distortionScale])
  useEffect(() => { if (water) water.waterColor.value.set(waterColor) }, [water, waterColor])
  useEffect(() => { if (water) water.sunColor.value.set(sunColor) }, [water, sunColor])

  // WaterMesh drives its wave scroll from TSL's global `time` node and exposes no speed option,
  // so drive that node's value directly. Halved from the previous scroll rate.
  useFrame((_, delta) => {
    if (time?.value !== undefined) {
      time.value += delta * animationSpeed * 5
    }
  })

  // Dispose the geometry, the generated normal map, and the material (which owns the
  // reflector's internal render target) when this unmounts.
  useEffect(() => () => {
    water?.geometry?.dispose()
    water?.material?.dispose()
    waterNormals.dispose()
  }, [water, waterNormals])

  if (!water) return null

  return <primitive ref={meshRef} object={water} onUpdate={markOceanReady} />
}
